import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useLoad, act } from "@/lib/hooks";
import { api, fdatetime } from "@/lib/api";
import { PageHeader, Section, StatusBadge, EmptyRow, Field, StackTable, NativeSelect } from "@/components/common";
import { SessionStatus, remaining } from "@/pages/ops/OpsConsole";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth } from "@/context/AuthContext";
import { InternalUsers, INTERNAL_ROLES } from "./InternalUsers";

const BLANK = { name: "", email: "", password: "", role: "support_l1" };
export const ROLE_LABEL = { platform_ops: "Ops Admin", support_l1: "Support Agent (L1)", support_l2: "Support Specialist (L2)" };

function InboundTest() {
  const [f, setF] = useState({ from_email: "", subject: "", text: "" });
  const [res, setRes] = useState(null);
  const send = async () => { const r = await act(() => api.post("/platform/tickets/inbound-test", f), "Simulated email processed"); if (r) setRes(r); };
  return (
    <Section title="Support inbox test · agyin@pressops.net" testId="inbound-test-card">
      <div className="grid gap-3 p-5 sm:grid-cols-2">
        <Field label="From (sender email)"><Input data-testid="inbound-from-input" value={f.from_email} onChange={(e) => setF({ ...f, from_email: e.target.value })} /></Field>
        <Field label="Subject"><Input data-testid="inbound-subject-input" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} /></Field>
        <div className="sm:col-span-2"><Field label="Body"><Input data-testid="inbound-body-input" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} /></Field></div>
        <div className="flex flex-wrap items-center gap-3 sm:col-span-2"><Button data-testid="inbound-send-button" onClick={send} className="bg-[#13294B] hover:bg-[#1c3a66]">Simulate inbound email</Button>
          {res?.ticket && <span className="text-sm" data-testid="inbound-result">Created {res.ticket.number} · {res.ticket.tenant_name || "unmatched sender"} {res.ticket.triage_required && <StatusBadge tone="warning" label="needs triage" />}</span>}</div>
        <p className="text-xs text-slate-500 sm:col-span-2">Live email-to-ticket starts once RESEND_INBOUND_API_KEY and RESEND_INBOUND_WEBHOOK_SECRET are set and the Resend webhook points to /api/inbound/resend.</p>
      </div>
    </Section>
  );
}

function OperatorDialog({ onClose, onSaved }) {
  const [f, setF] = useState(BLANK);
  const save = async () => { if (await act(() => api.post("/platform/internal/operators", f), "Team member created")) { onSaved(); onClose(); } };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="operator-dialog">
        <DialogHeader><DialogTitle>New internal team member</DialogTitle><DialogDescription className="sr-only">Create an internal operations or support account and choose its role.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Field label="Role"><NativeSelect testId="operator-role-select" value={f.role} onChange={(v) => setF({ ...f, role: v })} options={Object.entries(INTERNAL_ROLES).map(([value, label]) => ({ value, label }))} /></Field>
          {[["name", "Full name", "text"], ["email", "Email", "email"], ["password", "Temporary password (min 8 characters)", "password"]].map(([k, l, t]) => (
            <Field key={k} label={l}>{t === "password" ? <PasswordInput data-testid={`operator-${k}-input`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
              : <Input type={t} data-testid={`operator-${k}-input`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />}</Field>))}
        </div>
        <DialogFooter><Button data-testid="operator-save-button" onClick={save} className="bg-[#13294B] hover:bg-[#1c3a66]">Create team member</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function InternalTeam() {
  const [ops, reloadOps] = useLoad("/platform/internal/users");
  const me = useAuth().user;
  const [active] = useLoad("/platform/internal/sessions?active=true");
  const [open, setOpen] = useState(false);
  const sessionRows = (rows, testId, empty) => (
    <div className="overflow-x-auto"><StackTable data-testid={testId}>
      <thead><tr><th>Tenant</th><th>Operator</th><th>Reason</th><th>Started</th><th>Status</th><th className="po-num">Time remaining</th></tr></thead>
      <tbody>{rows?.length === 0 && <EmptyRow cols={6} text={empty} />}{rows?.map((s) => (
        <tr key={s.id} data-testid={`ops-session-${s.id}`}><td className="font-medium text-slate-900">{s.tenant_name}</td><td>{s.operator_name}</td><td className="max-w-xs text-slate-600">{s.reason}</td>
          <td>{fdatetime(s.started_at)}</td><td><SessionStatus s={s} />{s.break_glass && <StatusBadge tone="danger" label="break-glass" className="ml-1" />}</td>
          <td className="po-num">{remaining(s.seconds_remaining)}</td></tr>))}</tbody>
    </StackTable></div>
  );
  return (
    <>
      <PageHeader title="Internal team" subtitle="Ops Admins edit inside reason-logged, time-bound sessions. Support Agents (L1) work tickets with view-only Support Mode; Support Specialists (L2) can edit except settings, users, payroll and HR.">
        <Button data-testid="new-operator-button" onClick={() => setOpen(true)} className="bg-[#45BFB4] text-[#13294B] hover:bg-[#389F95] hover:text-white"><Plus className="mr-1 h-4 w-4" />New team member</Button>
      </PageHeader>
      <Section title={`Active ops sessions (${active?.length ?? 0})`} testId="active-sessions-panel">{sessionRows(active, "active-sessions-table", "No active sessions.")}</Section>
      <InternalUsers rows={ops} reload={reloadOps} me={me} />
      <InboundTest />
      {open && <OperatorDialog onClose={() => setOpen(false)} onSaved={reloadOps} />}
    </>
  );
}
